/**
 * Typing on phones and tablets.
 *
 * A phone only shows its on-screen keyboard while a text box has focus, and
 * the game draws on a canvas with none. So on touch devices a hidden text box
 * sits in the page; a tap on the game focuses it, the phone's keyboard opens,
 * and what the player types is turned into the same key events a real
 * keyboard sends - so every scene works unchanged.
 *
 * Phone keyboards rarely report real keys (Android sends "Unidentified" for
 * every letter), and many build a word up and rewrite it as you type. So the
 * keys are worked out from how the box's text changed instead: keyDiff below.
 *
 * It also fits the game into the part of the screen the keyboard leaves free,
 * instead of leaving it centred behind the keyboard.
 */

import { IS_PORTRAIT, LAYOUT, PORTRAIT_FOR_DEVICES } from '../types';

/** Kept at the start of the box, so Backspace on an "empty" box still registers. */
const SENTINEL = '>>';
/** Past this length the box is quietly emptied back to the sentinel. */
const MAX_VALUE = 40;

/**
 * The key presses that turn `before` into `after`: Backspaces for what was
 * removed from the end, then each new character. A space is the Space key.
 */
export function keyDiff(before: string, after: string): string[] {
  let common = 0;
  while (common < before.length && common < after.length && before[common] === after[common]) {
    common += 1;
  }
  const keys: string[] = [];
  for (let i = common; i < before.length; i += 1) keys.push('Backspace');
  for (const char of after.slice(common)) keys.push(char === ' ' ? ' ' : char);
  return keys;
}

/** The same events a hardware keyboard would send for `key`. */
export function sendKey(key: string): void {
  const code =
    key === ' '
      ? 'Space'
      : key === 'Backspace' || key === 'Enter'
        ? key
        : /^[a-z]$/i.test(key)
          ? `Key${key.toUpperCase()}`
          : '';
  const keyCode =
    key === ' '
      ? 32
      : key === 'Backspace'
        ? 8
        : key === 'Enter'
          ? 13
          : key.length === 1
            ? key.toUpperCase().charCodeAt(0)
            : 0;
  for (const type of ['keydown', 'keyup'] as const) {
    window.dispatchEvent(
      new KeyboardEvent(type, { key, code, keyCode, bubbles: true, cancelable: true }),
    );
  }
}

/** A phone or tablet: a coarse pointer and no hover. Touch laptops don't count. */
export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches
  );
}

let input: HTMLInputElement | null = null;
let wanted = false;

/**
 * Whether the current screen takes typing (a level, or entering a name). While
 * it does, a tap on the game opens the phone keyboard; when it stops, the
 * keyboard is put away. A no-op on computers.
 */
export function setTouchTyping(active: boolean): void {
  wanted = active;
  if (input === null) return;
  if (!active) input.blur();
}

/** True once the phone keyboard is open for the game. */
export function touchTypingOpen(): boolean {
  return input !== null && document.activeElement === input;
}

/** Call once at startup. Does nothing on computers. */
export function installTouchKeyboard(host: HTMLElement): void {
  if (!isTouchDevice() || input !== null) return;

  const box = document.createElement('input');
  box.type = 'text';
  box.value = SENTINEL;
  box.setAttribute('autocomplete', 'off');
  box.setAttribute('autocorrect', 'off');
  box.setAttribute('autocapitalize', 'off');
  box.setAttribute('spellcheck', 'false');
  box.setAttribute('enterkeyhint', 'go');
  box.setAttribute('aria-label', 'Type here');
  // On screen but invisible: a box moved off screen is refused focus by some
  // phones. 16px stops iOS zooming the page when it is focused.
  Object.assign(box.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    fontSize: '16px',
    border: '0',
    padding: '0',
    caretColor: 'transparent',
  });
  document.body.appendChild(box);
  input = box;

  let last = SENTINEL;
  let composing = false;
  const settle = (): void => {
    if (composing || box.value.length <= MAX_VALUE) return;
    box.value = SENTINEL;
    last = SENTINEL;
  };

  box.addEventListener('compositionstart', () => {
    composing = true;
  });
  box.addEventListener('compositionend', () => {
    composing = false;
    settle();
  });
  box.addEventListener('input', () => {
    const now = box.value;
    // Backspace ate into the sentinel: that was a Backspace on an empty word.
    if (!now.startsWith(SENTINEL)) {
      const extra = now.length < SENTINEL.length ? '' : now.slice(SENTINEL.length);
      for (const key of keyDiff(last.slice(SENTINEL.length), extra)) sendKey(key);
      if (now.length < SENTINEL.length) sendKey('Backspace');
      box.value = SENTINEL;
      last = SENTINEL;
      return;
    }
    for (const key of keyDiff(last, now)) sendKey(key);
    last = now;
    // After a space the word is done; start the box afresh.
    if (now.endsWith(' ') && !composing) {
      box.value = SENTINEL;
      last = SENTINEL;
    }
    settle();
  });

  // The phone's own key events must not reach the game as well, or iPhones
  // (which do report real keys) would type every letter twice. Enter is the
  // one key with no text change, so it is passed on by hand.
  const swallow = (event: KeyboardEvent): void => {
    event.stopPropagation();
    if (event.type === 'keydown' && event.key === 'Enter') {
      event.preventDefault();
      sendKey('Enter');
      box.value = SENTINEL;
      last = SENTINEL;
    }
  };
  box.addEventListener('keydown', swallow);
  box.addEventListener('keyup', swallow);

  // A tap is the only moment a phone allows the keyboard to open.
  host.addEventListener('pointerup', () => {
    if (wanted && document.activeElement !== box) box.focus({ preventScroll: true });
  });

  // Wide stage: fit the game into what the keyboard leaves visible, anchored
  // to the top. Tall stage: stay full screen and let the keyboard cover the
  // bottom, which each screen keeps clear; only stop the page being scrolled.
  // iOS ignores interactive-widget and shrinks the page under the keyboard, so
  // the tall stage pins its height to the tallest the screen has been.
  const viewport = window.visualViewport;
  let tallest = 0;
  const fit = (): void => {
    if (IS_PORTRAIT) {
      tallest = Math.max(tallest, window.innerHeight, viewport?.height ?? 0);
      const pinned = `${Math.round(tallest)}px`;
      if (host.style.height !== pinned) {
        host.style.height = pinned;
        window.dispatchEvent(new Event('resize'));
      }
      window.scrollTo(0, 0);
      return;
    }
    const height = viewport?.height ?? window.innerHeight;
    host.style.height = `${Math.round(height)}px`;
    window.scrollTo(0, 0);
    window.dispatchEvent(new Event('resize'));
  };
  viewport?.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  fit();

  // Turning the device between upright and sideways needs the other stage
  // shape, which is chosen at startup: reload into it. Progress is saved after
  // every level, so at most the level in progress starts again.
  window.addEventListener('resize', () => {
    const upright = window.innerHeight > window.innerWidth;
    if (PORTRAIT_FOR_DEVICES && upright !== (LAYOUT === 'portrait')) window.location.reload();
  });
}
